import { CalendarToday } from "@mui/icons-material";
import {
  FormControl,
  FormHelperText,
  IconButton,
  Popover,
} from "@mui/material";
import { DateTimeValidationError, StaticDatePicker } from "@mui/x-date-pickers";
import { DateField } from "@mui/x-date-pickers/DateField";
import { FieldChangeHandler } from "@mui/x-date-pickers/internals";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import useEvent from "react-use-event-hook";
import styled from "styled-components";

import { useDynamicFormsTranslate, useRtl } from "@ryde/redux";
import {
  DATA_E2E_KEY,
  getRegisterAttributes,
  getUTCDateOrNowInISO,
  ICustomField,
  IStyledAttributes,
  MAIN_DATE_FORMAT,
  nowInTimezone,
  nowTDateISO,
  TimeZoneEnum,
  useTimezone,
  ValidationHelpers,
} from "@ryde/shared";

import { LocalizationProvider } from "../../common";

const DateEditable = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "date-editable",
})`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const resolveFieldValue = ({
  value,
  allowEmptyDate,
  timezone,
}: {
  value: unknown;
  allowEmptyDate: boolean;
  timezone: TimeZoneEnum;
}): dayjs.Dayjs | null => {
  if (value) {
    return dayjs.utc(value as dayjs.ConfigType);
  }

  return allowEmptyDate ? null : nowInTimezone(timezone).utc();
};

export const DateTemplate: FC<ICustomField> = ({
  id,
  name,
  formLabel,
  labelTranslated,
  formAttributes,
  formValidation,
  dateFormat,
  e2eData = "date-template",
}) => {
  const { control, getValues, setValue } = useFormContext();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });
  const { disabled } = getRegisterAttributes(formAttributes);
  const { timezone } = useTimezone();
  const { isRtl } = useRtl();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isPopoverOpen = Boolean(anchorEl);
  const popoverId = isPopoverOpen ? "date-template-popover" : undefined;
  const handleOpenPopover = useEvent(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      setAnchorEl(event.currentTarget);
    },
  );
  const handleClosePopover = useEvent((): void => {
    setAnchorEl(null);
  });
  const allowEmptyDate = Boolean(formAttributes?.allowEmptyDate);
  const minDate = useMemo(
    () =>
      formAttributes?.disablePastDates
        ? nowInTimezone(timezone).startOf("day")
        : undefined,
    [formAttributes?.disablePastDates, timezone],
  );

  useEffect(() => {
    // Disabled or conditionally disabled dates must not self-default to "now": marking the
    // form dirty on mount blocks the edit form's dto reset, leaving the whole panel empty.
    if (allowEmptyDate || disabled || formAttributes?.disableCondition) {
      return;
    }

    const initialValue = getValues(name);

    if (!initialValue) {
      setValue(name, nowTDateISO(timezone), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    allowEmptyDate,
    disabled,
    formAttributes?.disableCondition,
    getValues,
    name,
    setValue,
    dateFormat,
    timezone,
  ]);

  const commitValue = useEvent((newValueUTCDayJs: dayjs.Dayjs | null): void => {
    if (newValueUTCDayJs?.isValid()) {
      // Store an ISO string, not a Dayjs: react-hook-form compares against defaultValues to derive isDirty, and a Dayjs
      // instance does not compare as changed, which leaves Save disabled. getUTCDateOrNowInISO also trims the seconds
      // MUI appends invisibly, which otherwise make two visually identical dates compare unequal.
      setValue(
        name,
        getUTCDateOrNowInISO(dayjs.utc(newValueUTCDayJs), timezone),
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
      return;
    }

    // Clearing the input has to reach the form as an explicit null, otherwise the previous date is silently kept.
    if (allowEmptyDate && !newValueUTCDayJs) {
      setValue(name, null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  });
  const handleChange: FieldChangeHandler<
    dayjs.Dayjs | null,
    DateTimeValidationError
  > = useEvent(newValueUTCDayJs => {
    commitValue(newValueUTCDayJs);
  });
  // Commit on day click and dismiss: month/year navigation does not fire onChange, so this only runs on a real selection.
  const handlePickerChange = useEvent(
    (newValueUTCDayJs: dayjs.Dayjs | null): void => {
      commitValue(newValueUTCDayJs);
      setAnchorEl(null);
    },
  );
  const handlePickerAccept = useEvent(
    (newValueUTCDayJs: dayjs.Dayjs | null): void => {
      commitValue(newValueUTCDayJs);
      setAnchorEl(null);
    },
  );
  const requiredComputed = useMemo(() => {
    return ValidationHelpers.hasRequiredRule(formValidation);
  }, [formValidation]);

  return (
    <LocalizationProvider>
      <Controller
        control={control}
        name={name}
        render={({
          field: { onChange, value, ...restField },
          fieldState: { error },
        }) => {
          return (
            <FormControl
              required={requiredComputed}
              data-e2e-template="date-template"
              data-e2e-field-name={name}
              error={!!error}
            >
              <DateEditable>
                <DateField
                  dir="ltr"
                  {...restField}
                  data-e2e={e2eData}
                  data-e2e-id={`${name}_${id || ""}`}
                  label={formLabelTranslated}
                  required={requiredComputed}
                  onChange={handleChange}
                  value={resolveFieldValue({ value, allowEmptyDate, timezone })}
                  disabled={disabled}
                  minDate={minDate}
                  format={dateFormat ?? MAIN_DATE_FORMAT}
                  timezone={timezone}
                  slotProps={{
                    textField: {
                      error: !!error,
                    },
                  }}
                />
                {!disabled && (
                  <>
                    <IconButton
                      aria-label={formLabelTranslated}
                      data-e2e={`${e2eData}-calendar-button`}
                      onClick={handleOpenPopover}
                    >
                      <CalendarToday />
                    </IconButton>
                    <Popover
                      id={popoverId}
                      open={isPopoverOpen}
                      anchorEl={anchorEl}
                      onClose={handleClosePopover}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: isRtl ? "left" : "right",
                      }}
                    >
                      <StaticDatePicker
                        value={resolveFieldValue({
                          value,
                          allowEmptyDate,
                          timezone,
                        })}
                        timezone={timezone}
                        minDate={minDate}
                        onChange={handlePickerChange}
                        onAccept={handlePickerAccept}
                        onClose={handleClosePopover}
                      />
                    </Popover>
                  </>
                )}
              </DateEditable>
              {/*TODO: add translation solution*/}
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          );
        }}
      />
    </LocalizationProvider>
  );
};
